import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicApiUrl } from '../utils/publicApiUrl';

const API = publicApiUrl('/api/brief-digest');

export default function BriefDigestFeedback() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const ratingParam = searchParams.get('rating');

  const postedRatingRef = useRef(false);
  const [state, setState] = useState({
    loading: true,
    error: null,
    trendName: '',
    rating: null,
    toast: null
  });

  useEffect(() => {
    if (!token) {
      setState({
        loading: false,
        error: 'missing',
        trendName: '',
        rating: null,
        toast: null
      });
      return;
    }
    let cancelled = false;
    fetch(`${API}/feedback/status?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (!data.success) {
          setState({
            loading: false,
            error: data.error || 'Invalid link',
            trendName: '',
            rating: null,
            toast: null
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          trendName: data.trendName || 'This brief',
          rating: data.rating,
          toast: null
        });
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            loading: false,
            error: 'Something went wrong.',
            trendName: '',
            rating: null,
            toast: null
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || state.loading || state.error) return;
    if (ratingParam == null || ratingParam === '') return;
    const r = parseInt(ratingParam, 10);
    if (Number.isNaN(r) || r < 1 || r > 5) return;
    if (postedRatingRef.current) return;
    postedRatingRef.current = true;
    fetch(`${API}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, rating: r })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setState((s) => ({ ...s, rating: r, toast: 'Thanks — your rating was saved.' }));
        } else {
          postedRatingRef.current = false;
          setState((s) => ({ ...s, toast: data.error || 'Could not save rating.' }));
        }
      })
      .catch(() => {
        postedRatingRef.current = false;
        setState((s) => ({ ...s, toast: 'Could not save rating.' }));
      });
  }, [token, ratingParam, state.loading, state.error]);

  const postRating = (r) => {
    if (!token) return;
    fetch(`${API}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, rating: r })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setState((s) => ({ ...s, rating: r, toast: 'Rating saved.' }));
        } else {
          setState((s) => ({ ...s, toast: data.error || 'Could not save.' }));
        }
      })
      .catch(() => setState((s) => ({ ...s, toast: 'Could not save.' })));
  };

  const shell = (inner) => (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ecfeff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: 24
      }}
    >
      {inner}
    </div>
  );

  if (state.loading) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #e5e7eb',
            borderTopColor: '#0891b2',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }}
        />
        <p style={{ color: '#6b7280', margin: 0 }}>Loading…</p>
      </div>
    );
  }

  if (state.error === 'missing' || !token) {
    return shell(
      <div
        style={{
          maxWidth: 420,
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 4px 24px rgba(15,118,110,0.12)',
          textAlign: 'center'
        }}
      >
        <h1 style={{ margin: '0 0 12px', fontSize: 22, color: '#134e4a' }}>Brief rating</h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 15 }}>
          Open this page from the <strong>Friday brief</strong> email — the link is unique to you and each brief.
        </p>
      </div>
    );
  }

  if (state.error) {
    return shell(
      <div
        style={{
          maxWidth: 420,
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          textAlign: 'center'
        }}
      >
        <p style={{ margin: 0, color: '#b91c1c' }}>{state.error}</p>
      </div>
    );
  }

  return shell(
    <div
      style={{
        maxWidth: 480,
        width: '100%',
        background: '#fff',
        borderRadius: 16,
        padding: 28,
        boxShadow: '0 4px 24px rgba(15,118,110,0.1)',
        border: '1px solid #ccfbf1'
      }}
    >
      <h1 style={{ margin: '0 0 8px', fontSize: 22, color: '#134e4a' }}>{state.trendName}</h1>
      <p style={{ margin: '0 0 20px', fontSize: 14, color: '#6b7280' }}>
        How useful was this brief? Tap a star to save your rating.
      </p>
      {state.toast ? (
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#0f766e', fontWeight: 600 }}>{state.toast}</p>
      ) : null}
      <div style={{ marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => postRating(r)}
            style={{
              margin: '0 6px 0 0',
              padding: '10px 14px',
              borderRadius: 10,
              border: state.rating === r ? '2px solid #0891b2' : '1px solid #e5e7eb',
              background: state.rating === r ? '#ecfeff' : '#f9fafb',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 700,
              color: '#111827'
            }}
          >
            {r}★
          </button>
        ))}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
